import { getArticleDetail } from "@/api/rest/common";
import { useChannelStoreHook } from "@/store/modules/channel";
import { useDeploymentStoreHook } from "@/store/modules/deployment";
import { useUserStoreHook } from "@/store/modules/user";

const DEFAULT_TERMS_ARTICLE_ID_MAP = {
  userAgreement: {
    prod: {
      zh: "68d8f2446ba9916f7ddaa868",
      en: "68d8f1ff6ba9916f7dda9d99",
      zh_TW: "68d8f27bbdc0336f22b65a12",
      jp: "68d8f2ac7139735ea16ea3ac"
    },
    dev: {
      zh: "68d8d2f4b0a4966299adc6e3",
      en: "68d8d3edec4d61629c4a7ea3",
      zh_TW: "68d8d515ec4d61629c4a844b",
      jp: "68d8d671dd119f628b60b263"
    }
  },
  privacyPolicy: {
    prod: {
      zh: "68d8f2dfbe1b666f4b4c85b1",
      en: "68d8f364be46726f616a7278",
      zh_TW: "68d8f30bd3ee5d5e93e93adf",
      jp: "68d8f33a734cb45ea881ac89"
    },
    dev: {
      zh: "68d8d77c41323e6277e26d51",
      en: "68d8d721ac4e8062930b09b6",
      zh_TW: "68d8d7dc08d3b962d06fb92f",
      jp: "68d8d95e08d3b962d06fbb06"
    }
  }
};

const TERMS_TYPE_TO_DEPLOYMENT_KEY = {
  userAgreement: "service",
  privacyPolicy: "privacy"
};

function getCurrentLanguage() {
  const { language } = useUserStoreHook();
  return ["zh", "en", "zh_TW", "jp"].includes(language) ? language : "en";
}

function getCurrentEnv() {
  return import.meta.env.MODE === "development" ? "dev" : "prod";
}

function resolveArticleIdByLanguage(source, language) {
  if (!source) {
    return "";
  }

  if (typeof source === "string") {
    return source;
  }

  if (typeof source !== "object") {
    return "";
  }

  return source[language] || "";
}

function replaceTemplateTerms(content, language) {
  const channelStore = useChannelStoreHook();
  const templateTerms = channelStore.channel?.capabilities?.templateTerms;

  if (!templateTerms?.enabled || !content) {
    return content;
  }

  return content
    .replace(/{{Mini Program Name}}/g, templateTerms.miniProgramName || "")
    .replace(
      /{{Company Name}}/g,
      templateTerms.companyNameI18n?.[language] || ""
    )
    .replace(/{{email}}/g, templateTerms.email || "");
}

export function getTermsArticleId(type, articleId) {
  const deploymentStore = useDeploymentStoreHook();
  const language = getCurrentLanguage();
  const env = getCurrentEnv();

  const articleIdByLanguage = resolveArticleIdByLanguage(articleId, language);
  if (articleIdByLanguage) {
    return articleIdByLanguage;
  }

  const deploymentKey = TERMS_TYPE_TO_DEPLOYMENT_KEY[type];
  const deploymentArticleId = resolveArticleIdByLanguage(
    deploymentKey ? deploymentStore.terms?.[deploymentKey] : "",
    language
  );

  if (deploymentArticleId) {
    return deploymentArticleId;
  }

  return DEFAULT_TERMS_ARTICLE_ID_MAP[type]?.[env]?.[language] || "";
}

export async function getTermsArticleData(type, articleId) {
  const language = getCurrentLanguage();
  const id = getTermsArticleId(type, articleId);

  if (!id) {
    return null;
  }

  const res = await getArticleDetail(id);
  return {
    ...res,
    title: replaceTemplateTerms(res.title, language),
    content: replaceTemplateTerms(res.content, language)
  };
}

export const getUserAgreement = articleId =>
  getTermsArticleData("userAgreement", articleId);
export const getUserPrivacy = articleId =>
  getTermsArticleData("privacyPolicy", articleId);
